const DEFAULT_LANG = 'en-US';

const VOICE_LOAD_TIMEOUT = 2000;
const VOICE_POLL_INTERVAL = 200;
const VOICE_POLL_ATTEMPTS = 10;

const resolveWindow = () => {
  if (typeof window !== 'undefined') {
    return window;
  }
  return undefined;
};

class TextToSpeechManager {
  constructor() {
    this.window = resolveWindow();
    this.speechSynthesis = this.window?.speechSynthesis || null;
    this.voices = [];
    this.pendingVoiceResolvers = [];
    this.currentUtterance = null;
    this.voiceMap = new Map();
    this.audioUnlocked = false;

    if (this.speechSynthesis) {
      this.loadVoices();
      if (typeof this.speechSynthesis.addEventListener === 'function') {
        this.speechSynthesis.addEventListener('voiceschanged', () => this.loadVoices());
      } else {
        this.speechSynthesis.onvoiceschanged = () => this.loadVoices();
      }
    }
  }

  isSupported() {
    return !!this.speechSynthesis;
  }

  loadVoices() {
    if (!this.speechSynthesis) {
      return;
    }
    const loadedVoices = this.speechSynthesis.getVoices() || [];
    if (!loadedVoices.length) {
      setTimeout(() => this.resolveVoicePromises(), VOICE_LOAD_TIMEOUT);
      return;
    }
    this.voices = loadedVoices;
    this.voiceMap = new Map(
      loadedVoices.map(voice => [voice.lang, (this.voiceMap.get(voice.lang) || []).concat(voice)])
    );
    this.resolveVoicePromises();
  }

  resolveVoicePromises() {
    if (!this.pendingVoiceResolvers.length) return;
    const resolvers = [...this.pendingVoiceResolvers];
    this.pendingVoiceResolvers = [];
    resolvers.forEach(resolver => resolver(this.voices));
  }

  async waitForVoices() {
    if (this.voices.length || !this.speechSynthesis) {
      return this.voices;
    }
    return new Promise(resolve => {
      this.pendingVoiceResolvers.push(resolve);
      setTimeout(() => {
        this.loadVoices();
        this.resolveVoicePromises();
      }, VOICE_LOAD_TIMEOUT);
    });
  }

  getLanguages() {
    if (!this.voices.length) {
      return [{ code: DEFAULT_LANG, label: 'English (US)', voiceURI: null }];
    }
    const seen = new Map();
    this.voices.forEach(voice => {
      if (!seen.has(voice.lang)) {
        seen.set(voice.lang, { code: voice.lang, label: voice.name, voiceURI: voice.voiceURI });
      }
    });
    return Array.from(seen.values());
  }

  getVoiceForLanguage(lang) {
    if (!lang || !this.voiceMap.size) return null;
    const exact = this.voiceMap.get(lang);
    if (exact && exact.length) {
      return exact[0];
    }
    const langPrefix = lang.split('-')[0];
    for (const [voiceLang, voices] of this.voiceMap.entries()) {
      if (voiceLang.startsWith(langPrefix) && voices.length) {
        return voices[0];
      }
    }
    return this.voices[0] || null;
  }

  async ensureVoicesLoaded() {
    if (!this.speechSynthesis) return [];
    if (this.voices.length) return this.voices;

    for (let attempt = 0; attempt < VOICE_POLL_ATTEMPTS; attempt += 1) {
      this.loadVoices();
      if (this.voices.length) break;
      await new Promise(resolve => setTimeout(resolve, VOICE_POLL_INTERVAL));
    }
    return this.voices;
  }

  async speak(text, lang = DEFAULT_LANG) {
    const normalizedText = typeof text === 'string' ? text : String(text || '');
    if (!this.isSupported() || !normalizedText.trim()) {
      return false;
    }
    await this.ensureVoicesLoaded();

    return new Promise((resolve, reject) => {
      try {
        const synthesis = this.speechSynthesis;
        const isActive = synthesis?.speaking || synthesis?.pending;
        if (isActive) {
          synthesis.cancel();
        }
        if (synthesis?.paused) {
          this.speechSynthesis.resume();
        }
        const UtteranceCtor = this.window?.SpeechSynthesisUtterance
          || (typeof SpeechSynthesisUtterance !== 'undefined' ? SpeechSynthesisUtterance : null);
        if (!UtteranceCtor) {
          resolve(false);
          return;
        }

        const utterance = new UtteranceCtor(normalizedText);
        utterance.lang = lang || DEFAULT_LANG;
        utterance.volume = 1;
        utterance.rate = 1;
        utterance.pitch = 1;
        const matchingVoice = this.getVoiceForLanguage(lang);
        if (matchingVoice) {
          utterance.voice = matchingVoice;
        }

        const maxDuration = Math.min(12000, Math.max(1500, text.length * 80));
        const timeout = setTimeout(() => {
          if (this.currentUtterance === utterance) {
            this.speechSynthesis.cancel();
            this.currentUtterance = null;
          }
          resolve(false);
        }, maxDuration);

        utterance.onend = () => {
          clearTimeout(timeout);
          this.currentUtterance = null;
          resolve(true);
        };
        utterance.onerror = (event) => {
          clearTimeout(timeout);
          console.warn('Speech synthesis error:', event.error || event);
          this.currentUtterance = null;
          resolve(false);
        };
        this.currentUtterance = utterance;
        this.audioUnlocked = true;
        const speakNow = () => {
          try {
            synthesis.speak(utterance);
          } catch (err) {
            clearTimeout(timeout);
            this.currentUtterance = null;
            reject(err);
          }
        };
        if (isActive) {
          setTimeout(speakNow, 75);
        } else {
          speakNow();
        }
      } catch (err) {
        console.error('Failed to speak text:', err);
        reject(err);
      }
    });
  }

  unlockAudio() {
    if (!this.isSupported() || this.audioUnlocked) {
      return false;
    }
    const UtteranceCtor = this.window?.SpeechSynthesisUtterance
      || (typeof SpeechSynthesisUtterance !== 'undefined' ? SpeechSynthesisUtterance : null);
    if (!UtteranceCtor) {
      return false;
    }

    try {
      const utterance = new UtteranceCtor(' ');
      utterance.volume = 0;
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onend = () => {
        this.audioUnlocked = true;
      };
      utterance.onerror = () => {
        this.audioUnlocked = true;
      };
      this.speechSynthesis.cancel();
      this.speechSynthesis.speak(utterance);
      setTimeout(() => {
        this.audioUnlocked = true;
      }, 200);
      return true;
    } catch (err) {
      console.warn('Speech unlock failed:', err);
      return false;
    }
  }

  stop() {
    if (!this.speechSynthesis) return;
    if (this.currentUtterance) {
      this.speechSynthesis.cancel();
      this.currentUtterance = null;
    }
  }
}

const textToSpeechManager = new TextToSpeechManager();

export const getAvailableTTSLanguages = () => textToSpeechManager.getLanguages();
export default textToSpeechManager;
